import { Injectable } from "@angular/core";
import { MetaDefinition } from "@angular/platform-browser";
import moment from "moment";
import { BehaviorSubject, EMPTY, Observable, concat, of } from "rxjs";
import {
  catchError,
  finalize,
  map,
  shareReplay,
  take,
  tap,
} from "rxjs/operators";
import {
  CollaboratorLoginRequest,
  CollaboratorLoginResponse,
  MenuServiceParameterizationResponse,
  MenuServiceProduct,
  OrderCheckInfoResponse,
} from "../models/menu-service.model";
import { ApiService, ApiRequestOptions } from "./api.service";
import { DocumentService } from "./document.service";
import { ThemeService } from "./theme.service";

export interface GetProductsOptions extends ApiRequestOptions {
  forceRefresh?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class MenuService {
  private domainName = "MenuService";
  private hourFormat = "HH:mm:ss";
  private themeApplyed = new BehaviorSubject(false);
  public onThemeApplyed$ = this.themeApplyed.asObservable();
  private productsCache = new Map<number, MenuServiceProduct[]>();
  private productsCacheTime = new Map<number, number>();
  private productsInflight = new Map<number, Observable<MenuServiceProduct[]>>();
  private readonly productsCacheTtlMs = 3 * 60 * 1000;

  constructor(
    private apiService: ApiService,
    private documentService: DocumentService,
    private themeService: ThemeService
  ) {}

  getParameterization(
    friendlyUrl: string
  ): Observable<MenuServiceParameterizationResponse> {
    return this.apiService
      .get(`${this.domainName}/Configuration/${friendlyUrl}`)
      .pipe(
        map((parameterization: MenuServiceParameterizationResponse) => {
          if (
            parameterization.WeekBusinessHours &&
            parameterization.WeekBusinessHours.length > 0
          ) {
            parameterization.StoreIsOpen = this.storeIsOpen(parameterization);
          } else {
            parameterization.StoreIsOpen = true;
          }
          return parameterization;
        })
      );
  }

  setAppParameterization(
    parameterization: MenuServiceParameterizationResponse
  ) {
    if (
      parameterization.WeekBusinessHours &&
      parameterization.WeekBusinessHours.length > 0
    ) {
      parameterization.StoreIsOpen = this.storeIsOpen(parameterization);
    } else if (parameterization.StoreIsOpen == null) {
      parameterization.StoreIsOpen = true;
    }

    this.themeService.applyParameterizationColors(parameterization);

    this.documentService.setDocumentTitle(parameterization.CompanyName);

    const ogTitle: MetaDefinition = {
      property: "og:title",
      content: parameterization.CompanyName,
    };
    this.documentService.updateMetaTag(ogTitle, true);

    if (parameterization.CompanyLogoUrl) {
      const logo: MetaDefinition = {
        property: "og:image",
        content: parameterization.CompanyLogoUrl,
      };
      this.documentService.updateMetaTag(logo, true);
    }

    this.themeApplyed.next(this.themeService.isDarkMode());
  }

  collaboratorLogin(
    request: CollaboratorLoginRequest,
    options?: ApiRequestOptions,
  ): Observable<CollaboratorLoginResponse> {
    return this.apiService.post(
      `${this.domainName}/Collaborator/Login`,
      request,
      { skipErrorToast: true, ...options },
    );
  }

  getOrderCheckInfo(
    companyId: number,
    orderCheckCode: number,
    options?: ApiRequestOptions,
  ): Observable<OrderCheckInfoResponse> {
    return this.apiService.get(
      `${this.domainName}/OrderCheck/${companyId}/${orderCheckCode}`,
      undefined,
      { skipLoading: true, skipErrorToast: true, ...options },
    );
  }

  getProducts(
    companyId: number,
    languageCode: string,
    options?: GetProductsOptions,
  ): Observable<MenuServiceProduct[]> {
    const { forceRefresh = false, ...requestOptions } = options ?? {};
    const cached = this.productsCache.get(companyId);

    if (forceRefresh) {
      return this.fetchProductsFromApi(
        companyId,
        languageCode,
        requestOptions,
      ).pipe(map((products) => this.copyProducts(products)));
    }

    if (cached) {
      const cacheAgeMs =
        Date.now() - (this.productsCacheTime.get(companyId) ?? 0);
      if (cacheAgeMs < this.productsCacheTtlMs) {
        return of(this.copyProducts(cached));
      }

      return concat(
        of(this.copyProducts(cached)),
        this.fetchProductsFromApi(companyId, languageCode, {
          skipLoading: true,
          skipErrorToast: true,
          ...requestOptions,
        }).pipe(
          map((products) => this.copyProducts(products)),
          catchError(() => EMPTY),
        ),
      );
    }

    return this.fetchProductsFromApi(
      companyId,
      languageCode,
      requestOptions,
    ).pipe(map((products) => this.copyProducts(products)));
  }

  hasCachedProducts(companyId: number): boolean {
    return this.productsCache.has(companyId);
  }

  prefetchProducts(
    companyId: number,
    languageCode: string | null = null,
  ): void {
    if (!companyId || this.productsCache.has(companyId)) {
      return;
    }

    this.fetchProductsFromApi(companyId, languageCode, {
      skipLoading: true,
      skipErrorToast: true,
    })
      .pipe(take(1))
      .subscribe({ error: () => undefined });
  }

  clearProductsCache(companyId?: number): void {
    if (companyId == null) {
      this.productsCache.clear();
      this.productsCacheTime.clear();
      this.productsInflight.clear();
      return;
    }
    this.productsCache.delete(companyId);
    this.productsCacheTime.delete(companyId);
    this.productsInflight.delete(companyId);
  }

  private fetchProductsFromApi(
    companyId: number,
    languageCode: string | null,
    requestOptions?: ApiRequestOptions,
  ): Observable<MenuServiceProduct[]> {
    const inflight = this.productsInflight.get(companyId);
    if (inflight) {
      return inflight;
    }

    const request$ = this.apiService
      .post(
        `${this.domainName}/ProductsV2`,
        {
          companyId,
          languageCode: languageCode
            ? languageCode.toUpperCase()
            : languageCode,
          IsWaiter: true,
        },
        { skipErrorToast: true, ...requestOptions, skipLoading: true },
      )
      .pipe(
        tap((products: MenuServiceProduct[]) => {
          this.productsCache.set(companyId, products);
          this.productsCacheTime.set(companyId, Date.now());
        }),
        finalize(() => this.productsInflight.delete(companyId)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.productsInflight.set(companyId, request$);
    return request$;
  }

  private copyProducts(products: MenuServiceProduct[]): MenuServiceProduct[] {
    return structuredClone(products ?? []);
  }

  storeIsOpen(parameterization: MenuServiceParameterizationResponse): boolean {
    let storeIsOpen = true;
    const weekDay = moment().format("dddd");

    if (parameterization.WeekBusinessHours) {
      const weekBusinessDay = parameterization.WeekBusinessHours.filter(
        (n) => n.WeekdayName.toLowerCase() === weekDay.toLowerCase()
      );
      const now = moment();

      if (weekBusinessDay.length > 0) {
        storeIsOpen = weekBusinessDay.some((n) => {
          const start = moment(n.StartTime, this.hourFormat);
          const end = moment(n.EndTime, this.hourFormat);

          if (end.get("hour") < start.get("hour")) {
            return now.isBetween(start, end.add(1, "days"), null, "[]");
          } else {
            return now.isBetween(start, end, null, "[]");
          }
        });
      }

      if (!storeIsOpen) {
        const yesterday = moment().subtract(1, "days").format("dddd");

        const weekBusinessDay = parameterization.WeekBusinessHours.filter(
          (n) =>
            n.WeekdayName.toLowerCase() === yesterday.toLowerCase() &&
            moment(n.EndTime, this.hourFormat).get("hour") <
              moment(n.StartTime, this.hourFormat).get("hour")
        );

        if (weekBusinessDay.length > 0) {
          storeIsOpen = weekBusinessDay.some((n) => {
            const start = moment(n.StartTime, this.hourFormat).subtract(
              1,
              "days"
            );
            const end = moment(n.EndTime, this.hourFormat);

            return now.isBetween(start, end, null, "[]");
          });
        }
      }
    }

    return storeIsOpen;
  }

  overflowService(overflow: boolean = true) {
    const $html = document.querySelector("html");
    if (!$html) {
      return;
    }
    if (overflow) {
      $html.style.overflow = "auto";
    } else {
      $html.style.overflow = "hidden";
    }
  }
}
